import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import SearchInput from '../SearchInput/SearchInput';
import './UserSearchPicker.scss';

const DEBOUNCE_MS = 300;
const MAX_VISIBLE_CHIPS = 50;

/** Searches users remotely and manages a controlled list of selected users. */
const UserSearchPicker = props => {
  const { selectedUsers, onChange, searchUsers, label, placeholder, autoFocus, showUserIds, hideSelectedList } = props;
  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);

  const debouncedSearch = useMemo(
    () =>
      _.debounce(async query => {
        if (!query.trim()) {
          setResults([]);
          setIsLoading(false);
          return;
        }
        try {
          setResults((await searchUsers(query)) || []);
        } catch {
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      }, DEBOUNCE_MS),
    [searchUsers]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const selectedUserIds = useMemo(() => new Set(selectedUsers.map(user => user.id)), [selectedUsers]);
  const availableResults = results.filter(user => !selectedUserIds.has(user.id));
  const updateDropdownPosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
      zIndex: 9999
    });
  }, []);
  const handleSearchChange = event => {
    const value = event.target.value;
    setSearchValue(value);
    updateDropdownPosition();
    setShowDropdown(true);
    setIsLoading(true);
    debouncedSearch(value);
  };
  const clearSearch = () => {
    setSearchValue('');
    setResults([]);
    setShowDropdown(false);
    debouncedSearch.cancel();
  };
  const selectUser = user => {
    if (!selectedUserIds.has(user.id)) onChange([...selectedUsers, user]);
    clearSearch();
  };

  return (
    <div className='user-search-picker-outer'>
      {label && <label className='user-search-picker-label'>{label}</label>}
      <div className='user-search-picker-input' ref={wrapperRef}>
        <SearchInput
          value={searchValue}
          onChange={handleSearchChange}
          onClear={clearSearch}
          placeholder={placeholder}
          isLoading={isLoading}
          showSearchIcon
          autoFocus={autoFocus}
          onFocus={() => {
            updateDropdownPosition();
            if (results.length) setShowDropdown(true);
          }}
        />
      </div>

      {showDropdown &&
        searchValue.trim() &&
        ReactDOM.createPortal(
          <div className='user-search-picker-dropdown' style={dropdownStyle}>
            {availableResults.length
              ? availableResults.map(user => (
                  <button key={user.id} type='button' className='user-search-picker-result' onClick={() => selectUser(user)}>
                    <span className='user-search-picker-result-name'>{user.name || 'No name'}</span>
                    {user.username && <span className='user-search-picker-result-username'>@{user.username}</span>}
                    {showUserIds && <span className='user-search-picker-result-id'>#{user.id}</span>}
                  </button>
                ))
              : !isLoading && <div className='user-search-picker-no-results'>No users found</div>}
          </div>,
          document.body
        )}

      {!!selectedUsers.length && !hideSelectedList && (
        <div className='user-search-picker-selected-list'>
          {selectedUsers.slice(0, MAX_VISIBLE_CHIPS).map(user => (
            <div key={user.id} className='user-search-picker-chip'>
              <span className='user-search-picker-chip-label'>
                {user.name || user.username || `User ${user.id}`}
                {user.username && ` (@${user.username})`}
                {showUserIds && ` · #${user.id}`}
              </span>
              <button
                type='button'
                className='user-search-picker-chip-remove'
                onClick={() => onChange(selectedUsers.filter(selectedUser => selectedUser.id !== user.id))}
                aria-label={`Remove ${user.name || user.username || `User ${user.id}`}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
          {selectedUsers.length > MAX_VISIBLE_CHIPS && (
            <div className='user-search-picker-chip-overflow'>+{selectedUsers.length - MAX_VISIBLE_CHIPS} more</div>
          )}
        </div>
      )}
    </div>
  );
};

UserSearchPicker.propTypes = {
  selectedUsers: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  searchUsers: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  showUserIds: PropTypes.bool,
  hideSelectedList: PropTypes.bool
};

UserSearchPicker.defaultProps = {
  label: '',
  placeholder: 'Search by name, username, or email...',
  autoFocus: false,
  showUserIds: false,
  hideSelectedList: false
};

export default UserSearchPicker;
